/**
 * Confirming a delete, having first said what it will take.
 *
 * The preview comes from the server, not from what the page happens to have
 * loaded — a dialog that under-reports is worse than no dialog. For anything
 * with assets behind it, the name has to be typed: a graph with a thousand
 * records should not go on a misplaced click.
 */

import { useEffect, useState } from 'react'
import { Modal, Spinner } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { api } from '@/core/api/base.api.client'

export interface DeletionPreview {
  total: number
  counts: Record<string, number>
  tables_dropped: string[]
  summary: string[]
}

export function DeleteDialog({
  title,
  name,
  what,
  previewPath,
  onConfirm,
  onClose,
  onDeleted,
}: {
  title: string
  /** Typed to confirm when there is anything to lose. */
  name: string
  /** "graph", "node type", "relationship" — used in the prose. */
  what: string
  previewPath: string
  onConfirm: () => Promise<{ removed?: DeletionPreview } | unknown>
  onClose: () => void
  onDeleted?: () => void
}) {
  const toast = useToast()
  const [preview, setPreview] = useState<DeletionPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .get<DeletionPreview>(previewPath)
      .then((result) => {
        if (!cancelled) setPreview(result)
      })
      .catch(() => {
        // A preview that cannot load must not block the delete; it just means
        // confirming without the detail.
        if (!cancelled) setPreview(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [previewPath])

  const hasAssets = (preview?.total ?? 0) > 0
  const confirmed = !hasAssets || typed.trim() === name

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            disabled={busy || loading || !confirmed}
            onClick={async () => {
              setBusy(true)
              try {
                const result = (await onConfirm()) as { removed?: DeletionPreview }
                const removed = result?.removed
                toast.success(
                  `Deleted ${name}.`,
                  removed?.summary?.length ? `Also removed ${removed.summary.join(', ')}.` : undefined,
                )
                onDeleted?.()
                onClose()
              } catch (error) {
                toast.error(error)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Deleting…' : `Delete ${what}`}
          </button>
        </>
      }
    >
      <div className="stack">
        <p style={{ margin: 0 }}>
          Delete <strong>{name}</strong>? This cannot be undone.
        </p>

        {loading && (
          <div className="row">
            <Spinner /> <span className="muted small">Checking what else this would remove…</span>
          </div>
        )}

        {!loading && preview && hasAssets && (
          <div className="callout callout-danger">
            <p className="callout-title">This also deletes</p>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {preview.summary.map((line) => (
                <li key={line} className="small">
                  {line}
                </li>
              ))}
            </ul>
            {preview.tables_dropped.length > 0 && (
              <p className="small" style={{ marginTop: 6 }}>
                And drops {preview.tables_dropped.length === 1 ? 'the table' : 'the tables'}{' '}
                {preview.tables_dropped.map((t) => (
                  <code key={t} className="mono">
                    {t}{' '}
                  </code>
                ))}
              </p>
            )}
          </div>
        )}

        {!loading && preview && !hasAssets && (
          <p className="muted small" style={{ margin: 0 }}>
            Nothing else depends on it — there is no data to lose.
          </p>
        )}

        {!loading && !preview && (
          <p className="muted small" style={{ margin: 0 }}>
            Could not check what else this would remove. Anything belonging to it goes with it.
          </p>
        )}

        {hasAssets && (
          <div className="field">
            <label className="field-label" htmlFor="confirm-name">
              Type <code className="mono">{name}</code> to confirm
            </label>
            <input
              id="confirm-name"
              className="input"
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}
